import React from "react";
import { Link } from "react-router-dom";
import PATHS from "../../constants/Paths";

/**
 * LegalLayout - shared shell + typography primitives for public legal pages
 * (Privacy Policy, Terms of Service).
 *
 * Extracted so the two documents share one layout and one type scale instead of
 * duplicating a near-identical page twice.
 */

export const H2 = ({ id, children }) => (
  <h2 id={id} className="scroll-mt-24 mt-10 mb-2 text-lg sm:text-xl font-semibold text-foreground">
    {children}
  </h2>
);

export const H3 = ({ children }) => (
  <h3 className="mt-6 mb-1 text-[15px] sm:text-base font-semibold text-foreground">{children}</h3>
);

export const P = ({ children }) => (
  <p className="mt-3 text-[15px] leading-7 text-muted-foreground">{children}</p>
);

export const UL = ({ children }) => (
  <ul className="mt-3 space-y-2 list-disc pl-5 text-[15px] leading-7 text-muted-foreground">{children}</ul>
);

export const Note = ({ children }) => (
  <div className="mt-4 rounded-xl border border-brand-200 bg-brand-50 p-4 text-[15px] leading-7 text-brand-900 dark:bg-brand-500/10 dark:border-brand-500/30 dark:text-brand-200">
    {children}
  </div>
);

export const Table = ({ head = [], rows = [] }) => (
  <div className="mt-4 overflow-x-auto rounded-xl border border-border">
    <table className="min-w-full divide-y divide-border text-[14px]">
      <thead className="bg-muted">
        <tr>
          {head.map((h) => (
            <th key={h} className="px-4 py-3 text-left font-semibold text-muted-foreground whitespace-nowrap">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-border">
        {rows.map((r, i) => (
          <tr key={i} className="align-top">
            {r.map((c, j) => (
              <td key={j} className="px-4 py-3 text-muted-foreground">{c}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function LegalLayout({ title, updated, summary, toc = [], children }) {
  return (
    <div className="min-h-screen bg-card text-foreground">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-border bg-card/90 backdrop-blur-md">
        <nav className="mx-auto flex h-16 max-w-4xl items-center justify-between px-4 sm:px-6">
          <Link to={PATHS.LANDING} className="flex items-center gap-2.5">
            <img src="/favicon.svg" alt="" width="32" height="32" className="h-8 w-8" />
            <span className="text-lg font-semibold tracking-tight text-foreground">RepCheck</span>
          </Link>
          <Link to={PATHS.LANDING} className="text-sm font-medium text-brand-600 hover:text-brand-700">
            ← Back to site
          </Link>
        </nav>
      </header>

      <main className="mx-auto max-w-4xl px-4 sm:px-6 py-10 sm:py-14">
        <h1 className="text-2xl sm:text-4xl font-semibold tracking-tight text-foreground">{title}</h1>
        <p className="mt-2 text-sm text-muted-foreground">Last updated: {updated}</p>
        {summary && <p className="mt-5 text-[15px] leading-7 text-muted-foreground">{summary}</p>}

        {toc.length > 0 && (
          <nav className="mt-8 rounded-xl border border-border bg-muted p-4 sm:p-5">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Contents</p>
            <ol className="mt-3 grid gap-x-6 gap-y-1.5 sm:grid-cols-2 text-[14px]">
              {toc.map((t, i) => (
                <li key={t.id}>
                  <a href={`#${t.id}`} className="text-muted-foreground hover:text-brand-700">
                    <span className="text-muted-foreground">{i + 1}.</span> {t.label}
                  </a>
                </li>
              ))}
            </ol>
          </nav>
        )}

        <article className="mt-4">{children}</article>

        <div className="mt-14 border-t border-border pt-6 flex flex-wrap items-center justify-between gap-3 text-sm">
          <div className="flex gap-4">
            <Link to={PATHS.PRIVACY} className="text-brand-600 hover:text-brand-700">Privacy Policy</Link>
            <Link to={PATHS.TERMS} className="text-brand-600 hover:text-brand-700">Terms of Service</Link>
          </div>
          <p className="text-muted-foreground">&copy; {new Date().getFullYear()} RepCheck</p>
        </div>
      </main>
    </div>
  );
}
